import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { EmptyStateComponent } from '../../../../atoms/empty-state/empty-state.component';
import { LoadingInlineComponent } from '../../../../atoms/loading-inline/loading-inline.component';
import { VideoWithControlsComponent } from '../../../../atoms/video-with-controls/video-with-controls.component';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { VideoFormatEnum } from '../../../../common/general-types/briefs.dto';
import { VideoCreatorResponseDto } from '../../../../common/general-types/videos.dto';
import { ImageService } from '../../../../services/image/image.service';
import { S3uploaderService } from '../../../../services/s3uploader/s3uploader.service';
import { VideoService } from '../../../../services/video/video.service';

@Component({
  selector: 'mb-content-library',
  imports: [
    EmptyStateComponent,
    LoadingInlineComponent,
    VideoWithControlsComponent,
  ],
  templateUrl: './content-library.html',
  styleUrl: './content-library.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentLibraryComponent implements OnInit, OnDestroy {
  readonly pageSize = 15;
  readonly VideoFormatEnum = VideoFormatEnum;

  videos = signal<VideoCreatorResponseDto[]>([]);
  page = signal(0);
  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';

  private readonly subscriptionList = new Subscription();
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly videoService = inject(VideoService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly uploadService = inject(S3uploaderService);
  private readonly router = inject(Router);
  readonly imageService = inject(ImageService);

  ngOnInit(): void {
    this.loadMediaLibrary();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  loadMediaLibrary(): void {
    const businessAccountId = this.currentUserService.focussedBrand?.id;
    if (!businessAccountId) {
      this.loadingState = 'error';
      this.refreshView();
      return;
    }

    this.loadingState = 'loading';
    this.refreshView();

    this.subscriptionList.add(
      this.videoService
        .getMediaLibrary(businessAccountId, this.pageSize, this.page() * this.pageSize)
        .subscribe({
          next: (videos) => {
            this.videos.set(videos ?? []);
            this.loadingState = 'loaded';
            this.refreshView();
          },
          error: (err) => {
            console.error('Content library load failed:', err);
            this.videos.set([]);
            this.loadingState = 'error';
            this.refreshView();
          },
        }),
    );
  }

  nextPage(direction: -1 | 1): void {
    const next = this.page() + direction;
    if (next < 0) {
      return;
    }
    this.page.set(next);
    this.videos.set([]);
    this.loadMediaLibrary();
  }

  goToViewSubmission(briefId: string, creatorUid: string): void {
    void this.router.navigateByUrl(`/view-submission/${briefId}/${creatorUid}`);
  }

  downloadContent(video: VideoCreatorResponseDto): void {
    if (video.fullUrl.includes('firebasestorage.googleapis.com')) {
      void this.uploadService.progressFirebaseDownload(video.fullUrl);
    } else {
      this.uploadService.secureVideoDownload(video.fullUrl, video.videoName);
    }
  }

  handleEmptyStateAction(action: 'confirm' | 'cancel'): void {
    if (action === 'confirm') {
      void this.router.navigate(['/create-brief-select']);
    }
  }

  briefTitle(video: VideoCreatorResponseDto): string | undefined {
    const brief = video.brief as { title?: string; briefName?: string } | null | undefined;
    return brief?.title || brief?.briefName || undefined;
  }

  creatorInitial(lastName: string | undefined): string {
    return lastName?.charAt(0) ?? '';
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }
}
